// @dx ds/domain — static tag + pill chip factories driven by `@ds/tokens/domainTones`.
//
// Two complementary factories:
//   - `createStaticTag`  — Tag chrome (square-corner, RADIUS.xs). Use in table cells /
//                          list rows where Pill chrome would be visually heavy.
//   - `createPillChip`   — Pill chrome (RADIUS.pill). Use in KV rows / entity headers /
//                          status strips where rounded pill style is canonical.
//
// Every visual value comes from `@ds/tokens/palette` via the Tag / Pill / StatusChipShell
// primitives. No hex codes, no inline colors.
//
// Split out of `EnumTag.tsx` so that module exports components only.
import React from "react";
import { useTranslation } from "react-i18next";
import { Tag, type TagTone } from "@ds/display/Tag";
import { StatusChipShell } from "@ds/display/StatusChipShell";
import { EmptyValue } from "@ds/display/EmptyValue";
import type { PillSize, PillTone } from "@ds/display/Pill";

export interface StaticTagBaseProps {
  size?: "sm" | "md";
  testId?: string;
}

/**
 * Factory: Tag chrome (square-corner, RADIUS.xs).
 * Preferred for table cells and list rows where Pill chrome would be too heavy.
 */
export function createStaticTag<T extends string>(
  colors: Record<string, TagTone>,
  translationPrefix: string,
  fallback: React.ReactNode = <EmptyValue />,
  valueAsFallback = false,
) {
  const Component: React.FC<StaticTagBaseProps & { value?: T | string | null }> = ({
    value, size = "md", testId,
  }) => {
    const { t } = useTranslation();
    if (!value) return <>{fallback}</>;
    const labelText = valueAsFallback
      ? t(`${translationPrefix}.${value}`, value)
      : t(`${translationPrefix}.${value}`);
    return (
      <Tag label={labelText} tone={colors[value] ?? "grey"} size={size} testId={testId} />
    );
  };
  return Component;
}

export function createPlainTag(tone?: TagTone) {
  const Component: React.FC<StaticTagBaseProps & { value?: string | null }> = ({
    value, size = "md", testId,
  }) => {
    if (!value) return <EmptyValue />;
    return <Tag label={value} tone={tone ?? "grey"} size={size} testId={testId} />;
  };
  return Component;
}

export interface PillChipBaseProps {
  size?: PillSize;
  variant?: "chip" | "inline";
  testId?: string;
}

/**
 * Factory: Pill chrome (RADIUS.pill, soft tone fill).
 * Preferred for KV rows, entity headers, and status strips.
 * Delegates to StatusChipShell so null/undefined emit an em-dash automatically.
 */
export function createPillChip<T extends string>(
  colors: Record<string, PillTone>,
  translationPrefix: string,
  options: { valueAsFallback?: boolean; defaultTestId?: string } = {},
) {
  const { valueAsFallback = true, defaultTestId } = options;
  const Component: React.FC<PillChipBaseProps & { value?: T | null }> = ({
    value, size = "md", variant = "chip", testId = defaultTestId,
  }) => {
    const { t } = useTranslation();
    const tone: PillTone | null = value ? (colors[value] ?? "grey") : null;
    const label: string | null = value
      ? (valueAsFallback ? t(`${translationPrefix}.${value}`, value) : t(`${translationPrefix}.${value}`))
      : null;
    return <StatusChipShell tone={tone} label={label} variant={variant} size={size} testId={testId} />;
  };
  return Component;
}
